<template>
    <div class="work-order-container">
        <vue-html2pdf
            :show-layout="false"
            :float-layout="true"
            :enable-download="false"
            :preview-modal="false"
            :paginate-elements-by-height="1400"
            :filename="`work_order_${activeWorkOrder.id}`"
            :pdf-quality="2"
            :manual-pagination="false"
            pdf-format="a4"
            pdf-orientation="landscape"
            :pdf-content-width="'100%'"
            ref="html2Pdf"
            :html-to-pdf-options="pdfOptions"
            @beforeDownload="beforeDownloadPdf"
        >
            <section slot="pdf-content">
                <work-order-view-table
                    :editMode="false"
                    :isPdfView="true"
                    v-if="activeWorkOrder && !loadingWorkOrderData"
                ></work-order-view-table>
            </section>
        </vue-html2pdf>
        <v-overlay :value="loadingWorkOrderData">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <div class="pa-5">
            <div class="d-flex">
                <h1 v-if="activeWorkOrder">{{ activeWorkOrder.production_order_number || "" }}</h1>
                <v-btn
                    @click="generatePdf"
                    :disabled="!activeWorkOrder || loadingWorkOrderData"
                    color="bal-dark-gray"
                    class="px-2 ml-4 mx-2"
                    style="min-width: 0"
                    dark
                >
                    <v-icon>print</v-icon>
                </v-btn>
            </div>

            <div
                class="d-flex align-baseline"
                v-if="
                    activeWorkOrder &&
                    !loadingWorkOrderData &&
                    ((hasPermissionForAction(PermissionsConstants.WORK_ORDER, PermissionsConstants.EDIT) &&
                        (user.work_roles.includes(PermissionsConstants.ROLE_HEAD_OF_SHIFT) ||
                            user.work_roles.includes(PermissionsConstants.ROLE_OPERATOR))) ||
                        user.work_roles.includes(PermissionsConstants.ROLE_SUPERADMIN))
                "
            >
                <v-switch v-model="editModeSwitch" :label="t('Edit mode')"></v-switch>
                <div
                    v-if="
                        user.work_roles &&
                        (user.work_roles.includes(PermissionsConstants.ROLE_SUPERADMIN) ||
                            user.work_roles.includes(PermissionsConstants.ROLE_HEAD_OF_SHIFT) ||
                            user.work_roles.includes(PermissionsConstants.ROLE_OPERATOR))
                    "
                    class="ml-2"
                >
                    <h3
                        v-if="
                            !user.work_roles.includes(PermissionsConstants.ROLE_SUPERADMIN) &&
                            ['confirmed', 'inactive', 'cancelled'].includes(activeWorkOrder.status)
                        "
                    >
                        {{ `${t("Status")}: ${activeWorkOrder.status}` }}
                    </h3>
                    <v-select
                        v-else
                        :value="activeWorkOrder.status"
                        @change="updateWorkOrder($event, 'status')"
                        dense
                        hide-details
                        flat
                        item-text="title"
                        item-value="value"
                        class="text-field-item"
                        solo-inverted
                        :items="statusItems"
                    ></v-select>
                </div>
                <span
                    v-if="
                        user.role === 'superadmin' &&
                        activeWorkOrder.last_status_change &&
                        activeWorkOrder.last_status_change.created_by
                    "
                    class="ml-2"
                    >{{
                        `${t("Last status change")}: ${activeWorkOrder.last_status_change.created_by} (${
                            activeWorkOrder.last_status_change.created_at
                        })`
                    }}</span
                >
            </div>
            <!-- Planner input for the automatic batch name (BALTT-530); not part of the printed form. -->
            <div
                class="d-flex align-baseline mb-2"
                v-if="activeWorkOrder && !loadingWorkOrderData && canEditBatchFields"
            >
                <span class="mr-2">{{ "Batch shelf life (months)" | t }}</span>
                <v-select
                    :value="activeWorkOrder.batch_best_before_months"
                    @change="updateWorkOrder($event, 'batch_best_before_months')"
                    :items="batchBestBeforeMonthItems"
                    :disabled="areBatchFieldsLocked"
                    item-text="title"
                    item-value="value"
                    dense
                    hide-details
                    flat
                    solo-inverted
                    class="text-field-item batch-field"
                ></v-select>
                <span class="ml-4 mr-2">{{ "Batch format" | t }}</span>
                <v-select
                    :value="activeWorkOrder.batch_format_type"
                    @change="updateWorkOrder($event, 'batch_format_type')"
                    :items="batchFormatTypeItems"
                    :disabled="areBatchFieldsLocked"
                    item-text="title"
                    item-value="value"
                    dense
                    hide-details
                    flat
                    solo-inverted
                    class="text-field-item batch-field"
                ></v-select>
                <span v-if="areBatchFieldsLocked" class="ml-2 locked-hint">
                    {{ "Locked: production start time is set, the batch name is final" | t }}
                </span>
            </div>
            <work-order-view-table
                :roles="user.work_roles || []"
                :editMode="isEditModeActive"
                :isPdfView="false"
                v-if="activeWorkOrder && !loadingWorkOrderData"
            ></work-order-view-table>

            <work-order-view-operator-app v-if="activeWorkOrder && !loadingWorkOrderData" />
            <work-order-view-files v-if="activeWorkOrder && !loadingWorkOrderData" />
            <work-order-view-comments v-if="activeWorkOrder && !loadingWorkOrderData" />
            <work-order-view-logs v-if="activeWorkOrder && !loadingWorkOrderData" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState, mapGetters } from "vuex";
import VueHtml2pdf from "vue-html2pdf";
import WorkOrderViewTable from "../work-order/table/work-order-view-table";
import WorkOrderViewComments from "../work-order/comments/work-order-view-comments";
import WorkOrderViewLogs from "../work-order/logs/work-order-view-logs";
import WorkOrderViewFiles from "../work-order/files/work-order-view-files.vue";
import WorkOrderViewOperatorApp from "../work-order/operator-app/work-order-view-operator-app.vue";
import { PermissionsConstants } from "@/permissions";
import { BATCH_BEST_BEFORE_MONTH_OPTIONS, BATCH_FORMAT_TYPES } from "../../constants/workOrder";

export default {
    name: "work-order-view",
    components: {
        "work-order-view-table": WorkOrderViewTable,
        "work-order-view-comments": WorkOrderViewComments,
        "work-order-view-logs": WorkOrderViewLogs,
        "work-order-view-files": WorkOrderViewFiles,
        "work-order-view-operator-app": WorkOrderViewOperatorApp,
        VueHtml2pdf,
    },
    data() {
        return {
            role: "superadmin",
            editModeSwitch: false,
            roleTypes: [
                { title: this.t("Head of shift"), id: "head_of_shift" },
                { title: this.t("Operator"), id: "operator" },
                { title: this.t("Superadmin"), id: "superadmin" },
            ],
            pdfOptions: {
                margin: [20, 5, 6, 5], //top, left, buttom, right

                filename: `work_order_1`,

                image: {
                    type: "jpeg",
                    quality: 0.98,
                },

                enableLinks: false,

                html2canvas: {
                    scale: 2,
                    bottom: 2,
                },
                pagebreak: { mode: ["css"] },

                jsPDF: {
                    unit: "mm",
                    format: "a4",
                    orientation: "landscape",
                },
            },
        };
    },
    computed: {
        PermissionsConstants() {
            return PermissionsConstants;
        },
        ...mapState(["user"]),
        ...mapState("WorkOrderStore", ["users", "activeWorkOrder", "loadingWorkOrderData"]),
        ...mapGetters(["hasPermissionForAction"]),
        ...mapGetters("WorkOrderStore", [
            "isToasterLineNumber",
            "isPackingLineNumber",
            "isFryerOnlyWorkOrder",
            "isFryerWithPackingWorkOrder",
        ]),
        statusItems() {
            let items = [
                { title: this.t("Plan"), value: "plan" },
                { title: this.t("In progress"), value: "in_progress" },
                { title: this.t("Done"), value: "done" },
            ];

            if (this.user.role === "superadmin") {
                items.push(
                    { title: this.t("Confirmed"), value: "confirmed" },
                    { title: this.t("Cancelled"), value: "cancelled" }
                );
            }

            return items;
        },
        isEditModeActive() {
            return this.editModeSwitch;
        },
        // Batch fields are the planner's (head of shift) decision; superadmin can fix them too (BALTT-530).
        canEditBatchFields() {
            const roles = this.user.work_roles || [];
            return (
                roles.includes(PermissionsConstants.ROLE_SUPERADMIN) ||
                (roles.includes(PermissionsConstants.ROLE_HEAD_OF_SHIFT) &&
                    this.hasPermissionForAction(PermissionsConstants.WORK_ORDER, PermissionsConstants.EDIT))
            );
        },
        // The backend ignores the batch fields once the production start time is stored; show that state.
        areBatchFieldsLocked() {
            return !!this.activeWorkOrder.exchange_report_start_time;
        },
        batchBestBeforeMonthItems() {
            return [
                { title: "-", value: null },
                ...BATCH_BEST_BEFORE_MONTH_OPTIONS.map((months) => ({
                    title: String(months),
                    value: months,
                })),
            ];
        },
        batchFormatTypeItems() {
            return [
                { title: "-", value: null },
                ...BATCH_FORMAT_TYPES.map((type) => ({ title: type, value: type })),
            ];
        },
    },
    async mounted() {
        if (!this.users || this.users.length === 0) {
            await this.fetchWorkOrders({});
        }

        if (this.$route.params.workOrderId !== "new" && !this.activeWorkOrder?.id !== this.$route.params.workOrderId) {
            await this.getWorkOrder(this.$route.params.workOrderId);
            this.initMissingBatchChangeTimeValues();
        }
    },
    methods: {
        ...mapActions("WorkOrderStore", ["getWorkOrder", "fetchWorkOrders"]),
        ...mapMutations("WorkOrderStore", ["updateActiveWorkOrderProperty"]),
        generatePdf() {
            this.$refs.html2Pdf.generatePdf();
        },
        async beforeDownloadPdf({ html2pdf, options, pdfContent }) {
            // Add custom header to every page
            // Generate PDF with modified content
            const isToasterLineNumber = this.isToasterLineNumber;
            const isPackingLineNumber = this.isPackingLineNumber;
            const isFryerOnlyWorkOrder = this.isFryerOnlyWorkOrder;
            const isFryerWithPackingWorkOrder = this.isFryerWithPackingWorkOrder;
            await html2pdf()
                .from(pdfContent)
                .set(options)
                .toPdf()
                .get("pdf")
                .then(function (pdf) {
                    let totalPages = pdf.internal.getNumberOfPages();
                    let text = "Kehtiv alates: 14.08.2025 Kinnitanud: Aleksander Mulin";
                    let textBottom = "Versioon: 4 Koostas: Kairit Jaanverk";
                    let textRightCorner = "Dok nr: TK-01";
                    let textBold = "TÖÖKÄSK - VAHETUSRAPORT VAHVLILIIN";

                    if (isFryerOnlyWorkOrder) {
                        text = "Kehtiv alates: 08.08.2025 Kinnitanud: Aleksander Mulin";
                        textBottom = "Versioon: 1 Koostanud: Kairit Jaanverk";
                        textRightCorner = "Dok nr: TK-05";
                        textBold = "TÖÖKÄSK - VAHETUSRAPORT PRAADIMISLIIN LEIB";
                    } else if (isFryerWithPackingWorkOrder) {
                        text = "Kehtiv alates: 08.08.2025 Kinnitanud: Aleksander Mulin";
                        textBottom = "Versioon: 1 Koostanud: Kairit Jaanverk";
                        textRightCorner = "Dok nr: TK-04";
                        textBold = "TÖÖKÄSK - VAHETUSRAPORT PRAADIMISLIIN + POLARIS PAKKIMINE";
                    } else if (isToasterLineNumber && isPackingLineNumber) {
                        text = "Kehtiv alates: 08.08.2025 Kinnitanud: Aleksander Mulin";
                        textBottom = "Versioon: 1 Koostanud: Kairit Jaanverk";
                        textRightCorner = "Dok nr: TK-06";
                        textBold = "TÖÖKÄSK - VAHETUSRAPORT POPCORNI TOOTMINE + POLARIS PAKKIMINE";
                    } else if (isToasterLineNumber) {
                        text = "Kehtiv alates: 20.02.2025 Kinnitanud: Aleksander Mulin";
                        textBottom = "Versioon: 2 Koostanud: Kairit Jaanverk";
                        textRightCorner = "Dok nr: TK-02";
                        textBold = "TÖÖKÄSK - VAHETUSRAPORT POPCORNI TOOTMINE";
                    } else if (isPackingLineNumber) {
                        text = "Kehtiv alates: 14.08.2025 Kinnitanud: Aleksander Mulin";
                        textBottom = "Versioon: 3 Koostanud: Kairit Jaanverk";
                        textRightCorner = "Dok nr: TK-03";
                        textBold = "TÖÖKÄSK - VAHETUSRAPORT POLARIS PAKKEMASIN";
                    }

                    const imageUrl =
                        "data:image/jpeg;base64,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";

                    for (let i = 1; i <= totalPages; i++) {
                        pdf.setPage(i);
                        pdf.setFontSize(7);
                        const textWidth =
                            (pdf.getStringUnitWidth(text) * pdf.internal.getFontSize()) / pdf.internal.scaleFactor;

                        // Calculate the horizontal position to center the text
                        const xPosition = (pdf.internal.pageSize.getWidth() - textWidth) / 2;
                        const textBottomWidth =
                            (pdf.getStringUnitWidth(textBottom) * pdf.internal.getFontSize()) /
                            pdf.internal.scaleFactor;

                        // Calculate the horizontal position to center the text
                        const bottomXPosition = (pdf.internal.pageSize.getWidth() - textBottomWidth) / 2;
                        const textRightCornerWidth =
                            (pdf.getStringUnitWidth(textRightCorner) * pdf.internal.getFontSize()) /
                            pdf.internal.scaleFactor;

                        // Calculate the horizontal position to align the text to the right corner
                        const xPositionRightCorner = pdf.internal.pageSize.getWidth() - textRightCornerWidth - 5; // Assuming 4 units margin
                        pdf.setTextColor(1);
                        pdf.text(xPosition, 10, text);
                        pdf.text(bottomXPosition, 12.75, textBottom);
                        pdf.text(xPositionRightCorner, 10, textRightCorner);
                        //pdf.text(`${i}`, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 8);

                        pdf.setFontStyle("bold");
                        pdf.setFontSize(9);

                        const textBoldWidth =
                            (pdf.getStringUnitWidth(textBold) * pdf.internal.getFontSize()) / pdf.internal.scaleFactor;

                        // Calculate the horizontal position to center the text
                        const boldXPosition = (pdf.internal.pageSize.getWidth() - textBoldWidth) / 2;
                        pdf.text(boldXPosition, 16.5, textBold);
                        // Reset font style to normal for subsequent text
                        pdf.setFontStyle("normal");

                        // Add image to the PDF
                        const imgWidth = 35; // Adjust image width as needed
                        const imgHeight = 11; // Adjust image height as needed
                        const imgXPosition = 5; // Assuming 10 units margin from left
                        const imgYPosition = 8; // Assuming 10 units margin from top
                        pdf.addImage(imageUrl, "JPEG", imgXPosition, imgYPosition, imgWidth, imgHeight);
                    }
                    window.open(pdf.output("bloburl"), "_blank");
                });
        },
        updateWorkOrder(value, field) {
            this.updateActiveWorkOrderProperty({ field, value });
        },
        initMissingBatchChangeTimeValues() {
            for (let i = 1; i < this.activeWorkOrder.ingredients.length; i++) {
                const previousIngredient = this.activeWorkOrder.ingredients[i - 1];
                const currentIngredient = this.activeWorkOrder.ingredients[i];
                if (
                    previousIngredient.product_code === currentIngredient.product_code &&
                    !previousIngredient.batch_change_time
                ) {
                    previousIngredient.batch_change_time = "00:00";
                }
            }
        },
    },
};
</script>

<style scoped lang="scss">
.batch-field {
    max-width: 120px;
}

.locked-hint {
    color: #888;
    font-size: 12px;
}

.work-order-container {
    overflow: scroll;
    width: 100%;
}
</style>
